import {
  AdvisoryProjectArchiveDocument,
  AdvisoryProjectCreateDocument,
  AdvisoryProjectDeleteDocument,
  AdvisoryProjectGetByIdDocument,
  AdvisoryProjectListDocument,
  AdvisoryProjectListPaginatedDocument,
  AdvisoryProjectListProjectTypeDocument,
  AdvisoryProjectUnarchiveDocument,
  AdvisoryProjectUpdateDocument,
} from "api/generated";
import { Optional, PartialNullable, ScopeEnum, ResourceEnum } from "config";

import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  ICreateAdvisoryProjectVars,
  IUpdateAdvisoryProjectVars,
  IAdvisoryProjectModel,
  IAdvisoryProjectListPaginatedReturnType,
  IAdvisoryProjectListPaginatedVars,
} from "./types";

export async function getAdvisoryProjectListProjectType(): Promise<string[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryProjectListProjectTypeDocument,
      fetchPolicy: "cache-first",
    });

    if (res?.errors) throw res?.errors;

    return res?.data?.advisoryProjectListProjectType ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryProjectList(
  resource: ResourceEnum,
  scope: ScopeEnum
): Promise<
  {
    advisoryProjectId: number;
    name: string;
    type: string;
    assetSymbol: string;
  }[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryProjectListDocument,
      variables: {
        resource,
        scope,
      },
      fetchPolicy: "cache-first",
    });

    if (res?.errors) throw res?.errors;

    return res?.data?.advisoryProjectList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryProjectListPaginated(
  variables: IAdvisoryProjectListPaginatedVars
): Promise<IAdvisoryProjectListPaginatedReturnType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryProjectListPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    if (res?.errors) throw res?.errors;

    return res?.data?.advisoryProjectListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createAdvisoryProject(
  variables: ICreateAdvisoryProjectVars
): Promise<Optional<Pick<IAdvisoryProjectModel, "advisoryProjectId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryProjectCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict advisory project lists
    executeEviction(client, {
      typename: "AdvisoryProject",
      fieldNames: ["advisoryProjectListPaginated", "advisoryProjectList"],
    });

    return res?.data?.advisoryProjectCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateAdvisoryProject(
  variables: IUpdateAdvisoryProjectVars
): Promise<Optional<Pick<IAdvisoryProjectModel, "advisoryProjectId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryProjectUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + all lists + getById queries
    executeEviction(client, {
      typename: "AdvisoryProject",
      id: variables.advisoryProjectId.toString(),
      fieldNames: [
        "advisoryProjectListPaginated",
        "advisoryProjectList",
        "advisoryProjectGetById",
      ],
    });

    return res?.data?.advisoryProjectUpdate;
  } catch (error) {
    throw error;
  }
}

export async function archiveAdvisoryProject(
  advisoryProjectId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryProjectArchiveDocument,
      variables: {
        advisoryProjectId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict archived entity + all lists
    executeEviction(client, {
      typename: "AdvisoryProject",
      id: advisoryProjectId.toString(),
      fieldNames: ["advisoryProjectListPaginated", "advisoryProjectList"],
    });

    return Boolean(res?.data?.advisoryProjectArchive);
  } catch (error) {
    throw error;
  }
}

export async function deleteAdvisoryProject(
  advisoryProjectId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryProjectDeleteDocument,
      variables: {
        advisoryProjectId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict deleted entity + all lists
    executeEviction(client, {
      typename: "AdvisoryProject",
      id: advisoryProjectId.toString(),
      fieldNames: ["advisoryProjectListPaginated", "advisoryProjectList"],
    });

    return Boolean(res?.data?.advisoryProjectDelete);
  } catch (error) {
    throw error;
  }
}

export async function unarchiveAdvisoryProject(
  advisoryProjectId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryProjectUnarchiveDocument,
      variables: {
        advisoryProjectId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict unarchived entity + all lists
    executeEviction(client, {
      typename: "AdvisoryProject",
      id: advisoryProjectId.toString(),
      fieldNames: ["advisoryProjectListPaginated", "advisoryProjectList"],
    });

    return Boolean(res?.data?.advisoryProjectUnarchive);
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryProject(
  advisoryProjectId: number
): Promise<PartialNullable<IAdvisoryProjectModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryProjectGetByIdDocument,
      variables: {
        advisoryProjectId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.advisoryProjectGetById;
  } catch (error) {
    throw error;
  }
}
